import { AfterViewInit, Component, EventEmitter, Inject, Input, OnInit, Output, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { InsuranceService } from 'app/services/insurance.service';
import _ from 'lodash';
import { BontonPanel } from 'environments/bonton-panel';
import { CommonUtils } from 'app/utils/commonutils';


@Component({
  selector: 'app-add-traveller-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatIconModule,
    MatDialogModule,
    MatButtonModule
  ],
  templateUrl: './add-traveller-modal.component.html',
  styleUrls: ['./add-traveller-modal.component.scss']
})
export class AddTravellerModalComponent implements OnInit, AfterViewInit {
  @Output() OnClose = new EventEmitter;
  @Input() paxData: any;
  totalTraveller: number = 1;
  ageArr: number[] = [];
  totalTravellerArr: any[] = [1];
  selectedAges: any[] = [];
  age_list: number[] = [];
  record: any;
  isSearchSubmitted: boolean = false;
  bontonPanel = BontonPanel;
  is_mobile = CommonUtils.isMobile;
  is_tablet = CommonUtils.isTablet;
  modalHeight: any = '300px';

  constructor(
    @Optional() public matDialogRef: MatDialogRef<AddTravellerModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private insuranceService: InsuranceService

  ) {
    if (data) {
      this.record = _.cloneDeep(data);
    } else {
      this.record = {};
    }
  }

  /**
   * Component constructor - injects dialog reference, incoming data and services.
   * @param {MatDialogRef<AddTravellerModalComponent>} matDialogRef - Reference to the opened dialog instance.
   * @param {any} data - Data passed into the dialog via MAT_DIALOG_DATA.
   * @param {Router} router - Angular Router instance used to inspect current route.
   * @param {InsuranceService} insuranceService - Service providing insurance-related utilities and data.
   * @returns {void}
   */

  /**
   * Lifecycle hook called after data-bound properties are initialized.
   * - Initializes the selectedAges array and populates the available age options from the InsuranceService.
   * @returns {void}
   */
  ngOnInit() {
    this.selectedAges = new Array(this.totalTravellerArr.length).fill("");
    this.ageArr = this.insuranceService.ageArr;
  }

  /**
   * Lifecycle hook called after the component's view has been fully initialized.
   * - If the current route includes 'detail', reads previously saved search data from localStorage
   *   to pre-fill traveller counts and ages. Also applies incoming dialog payload (this.record) when present.
   * @returns {void}
   */
  ngAfterViewInit() {
    if (this.router.url.split("/").includes('detail')) {
      let localData = JSON.parse(localStorage.getItem('insurance') || '{}');
      this.age_list = localData?.searchFormData?.age_list || [];

      this.totalTravellerArr = this.age_list?.length > 0 ? this.generateNumArr(this.age_list.length) : this.totalTravellerArr;
      this.totalTraveller = this.totalTravellerArr.length;

      // Bind the values from this.age_list to selectedAges if they exist
      for (let i = 0; i < this.totalTravellerArr.length; i++) {
        this.selectedAges[i] = this.age_list[i] ?? "";
      }

    }

    if (this.paxData && this.paxData.data) {
      this.record = Object.assign(this.record || {}, this.paxData.data);
    }

    if (this.record.age_list?.length) {
      this.totalTraveller = this.record.age_list.length;
      this.totalTravellerArr = this.generateNumArr(this.record.age_list.length);
      this.selectedAges = this.record.age_list;
      this.isSearchSubmitted = this.record.submitted;
    }

  }

  /**
   * Generates an array of consecutive numbers from 1 to n.
   * @param {number} n - The length of the array to generate.
   * @returns {number[]} An array containing numbers [1..n].
   */
  generateNumArr(n: number) {
    let arr = []
    for (let i = 1; i <= n; i++) {
      arr.push(i);
    }
    return arr;
  }

  /**
   * Adds or removes a traveller entry based on the provided action.
   * - 'add' will increment the traveller count up to a maximum of 6 and append a blank age slot.
   * - any other value will attempt to remove a traveller if more than one exists.
   * @param {string} val - Action value ('add' to add a traveller, otherwise remove).
   * @returns {void}
   */
  onChangeTraveller(val: string) {
    if (val === 'add') {
      if (this.totalTraveller < 6) {
        this.totalTraveller++;
        this.selectedAges.push("")
      }
    } else {
      if (this.totalTraveller > 1) {
        this.totalTraveller--;
        this.selectedAges.pop()
        if (this.age_list && this.age_list.length) {
          this.age_list.pop()
        }
      }
    }

    this.totalTravellerArr = this.generateNumArr(this.totalTraveller);
  }

  /**
   * Handles change of age dropdown for a specific traveller.
   * @param {number} idx - Index of the traveller whose age changed.
   * @param {Event} event - The DOM event from the select control containing the new value.
   * @returns {void}
   */
  onAgeChange(idx: any, event: any) {
    const selectedAge = parseFloat(event.target.value);
    this.selectedAges[idx] = selectedAge;
  }

  applySelection() {
    this.isSearchSubmitted = true;
    const hasEmptyAges = this.selectedAges.some(age => age === "" || age === null || age === undefined);
    if (hasEmptyAges) {
      return;
    }

    const result = { selectedAges: this.selectedAges, key: 'done' };
    const isMobile = this.is_mobile();
    const isTablet = this.is_tablet();

    if ((isMobile || isTablet) && this.matDialogRef) {
      this.matDialogRef.close(result);
    } else {
      this.OnClose.emit(result);
    }
  }

  close(): void {
    const result = { selectedAges: this.selectedAges, key: 'close' };
    const isMobile = this.is_mobile();
    const isTablet = this.is_tablet();

    if ((isMobile || isTablet) && this.matDialogRef) {
      this.matDialogRef.close(result);
    } else {
      this.OnClose.emit(result);
    }
  }

}
